import { NookKind, useNooks, type NookFilters } from "@nook/api-client"
import { Button, Card, CardContent, CardDescription, CardTitle, cn, Skeleton } from "@nook/ui"
import { Link } from "@tanstack/react-router"
import { Bot, FileText, Image as ImageIcon, Link2, MessageCircle, Plus, SquarePen } from "lucide-react"
import { useEffect, useRef } from "react"

import { useOverlay } from "@/components/app-shell.js"
import { EmptyState, GhostCards } from "@/components/empty-state.js"
import { CARD_HEIGHT, NookCard } from "@/components/nook-card.js"
import { LIST_COLUMNS, NookRow } from "@/components/nook-row.js"
import type { LibraryView } from "@/lib/browse.js"

export const GRID = "grid grid-cols-1 gap-4 p-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"

/** The other doors into an empty library, besides the capture dialog. Both set up in Settings. */
const WAYS = [
  { icon: MessageCircle, title: "From WhatsApp", description: "Send links to Nook in a chat.", hash: "whatsapp" },
  { icon: Bot, title: "From Claude or Cursor", description: "Connect Nook over MCP.", hash: "mcp" },
] as const

function LoadingSkeleton({ view }: { view: LibraryView }) {
  return view === "grid" ? (
    <div className={GRID}>
      {Array.from({ length: 8 }, (_, index) => (
        <Skeleton key={index} className={`${CARD_HEIGHT} w-full rounded-xl`} />
      ))}
    </div>
  ) : (
    <div className="space-y-2 p-4">
      {Array.from({ length: 8 }, (_, index) => (
        <Skeleton key={index} className="h-8 w-full" />
      ))}
    </div>
  )
}

function Notice({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="p-6">
      <Card>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <CardTitle>{title}</CardTitle>
            {children}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * The library, newest first: a list or a grid. `selected` is the open nook or
 * the keyboard's cursor. A nook opens in the panel via `/?nook=`.
 */
export function NookGrid({ filters, view, selected }: { filters?: NookFilters; view: LibraryView; selected?: string | undefined }) {
  const nooks = useNooks(filters)
  const items = nooks.data?.pages.flatMap((page) => page.items) ?? []
  const sentinel = useRef<HTMLDivElement>(null)
  const overlay = useOverlay()
  const { hasNextPage, isFetching, fetchNextPage } = nooks

  // Infinite scroll over the cursor. Not while any fetch is running: a
  // `fetchNextPage` mid-refetch cancels that refetch.
  useEffect(() => {
    const target = sentinel.current
    if (!target || !hasNextPage || isFetching) return

    const observer = new IntersectionObserver(([entry]) => entry?.isIntersecting && void fetchNextPage(), {
      rootMargin: "400px",
    })
    observer.observe(target)
    return () => observer.disconnect()
  }, [hasNextPage, isFetching, fetchNextPage])

  if (nooks.isPending) return <LoadingSkeleton view={view} />

  if (nooks.isError) {
    return (
      <Notice title="Could not load your library">
        <CardDescription>Try reloading the page.</CardDescription>
      </Notice>
    )
  }

  const { kind, ...narrowing } = filters ?? {}
  if (items.length === 0 && Object.values(narrowing).some(Boolean)) {
    return (
      <Notice title="No nooks match these filters">
        <CardDescription>Try a wider date range or another source.</CardDescription>
      </Notice>
    )
  }

  if (items.length === 0 && kind === NookKind.Note) {
    return (
      <EmptyState
        art={<GhostCards icons={[SquarePen]} />}
        title="No notes yet"
        description="Jot down a thought. Nook connects it to everything."
        action={
          <Button asChild>
            <Link to="/n/$id" params={{ id: "new" }}>
              <SquarePen />
              New note
            </Link>
          </Button>
        }
      />
    )
  }

  if (items.length === 0) {
    return (
      <EmptyState
        art={<GhostCards icons={[Link2, FileText, ImageIcon]} />}
        title="Your library is empty"
        description="Save a link, a note or a screenshot. It shows up here, ready to ask about."
        action={
          <Button onClick={() => overlay("capture")}>
            <Plus />
            Nook something
          </Button>
        }
      >
        <div className="mt-4 grid w-full max-w-md gap-3 text-left sm:grid-cols-2">
          {WAYS.map(({ icon: Icon, title, description, hash }) => (
            <Link key={title} to="/settings" hash={hash} className="hover:bg-muted/50 space-y-1 rounded-lg border p-3 transition-colors">
              <Icon className="text-muted-foreground size-4" />
              <p className="text-sm font-medium">{title}</p>
              <p className="text-muted-foreground text-xs">{description}</p>
            </Link>
          ))}
        </div>
      </EmptyState>
    )
  }

  return (
    <>
      {view === "compact" ? (
        <div className={cn(LIST_COLUMNS, "glass text-muted-foreground sticky top-14 z-[5] border-b py-2.5 text-xs")}>
          <span>Title</span>
          <span>Source</span>
          <span>Type</span>
          <span className="text-right">Nooked</span>
        </div>
      ) : null}

      {view === "grid" ? (
        <div data-grid className={GRID}>
          {items.map((nook) => (
            <NookCard key={nook.id} nook={nook} selected={nook.id === selected} />
          ))}
        </div>
      ) : (
        items.map((nook) => <NookRow key={nook.id} nook={nook} selected={nook.id === selected} />)
      )}

      <div ref={sentinel}>{nooks.isFetchingNextPage ? <LoadingSkeleton view={view} /> : null}</div>
    </>
  )
}
